(() => {
  'use strict';
  const fold = value => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  function title(product) {
    const full = String(typeof product === 'string' ? product : product?.title || '').replace(/\s+/g, ' ').trim();
    // Retain every model, colour, dimension and standard; remove only repeated category wording.
    let short = full
      .replace(/\b(roues?|deck|guidon|fourche|collier|poign[eé]es?|grip|frein|roulements?|casque|genouill[eè]res?)\b([\s\S]*?)\s+trottinette\s+freestyle\b/gi, '$1$2')
      .replace(/\b(genouill[eè]res?|coudi[eè]res?|casque)\b([\s\S]*?)\s+trottinette\s+skate\s+roller\b/gi, '$1$2')
      .replace(/trottinette\s+(?:compl[eè]te\s+)?freestyle(?:\s+compl[eè]te)?/gi, 'Trottinette')
      .replace(/trottinette\s+compl[eè]te/gi, 'Trottinette')
      .replace(/trottinettes\s+freestyle/gi, 'Trottinettes')
      .replace(/\bskateboard\s+complet\s+skateboard\b/gi, 'Skateboard complet');
    if (/\bS2S\b/i.test(short) && /\bVersatyl\b/i.test(short)) short = short.replace(/\s+Scoot\s+2\s+Street\b/i, '');
    return short.replace(/\s+/g, ' ').trim() || full;
  }
  function choiceAdvice(product, variant, productData) {
    const name = fold(product.title), options = product.options || [];
    const optionName = o => typeof o === 'string' ? o : o.name;
    const selected = variant?.options || [variant?.option1, variant?.option2, variant?.option3];
    const pairs = options.map((o, i) => ({ label: optionName(o), value: selected[i] })).filter(o => o.value && o.value !== 'Default Title');
    if (productData?.completeScooter(product)) {
      const measurements = productData.rows(product, variant).filter(r => ['total_height', 'bar_height', 'deck_length', 'deck_width'].includes(r.key));
      const summary = measurements.filter(r => ['bar_height', 'deck_length'].includes(r.key)).slice(0, 2);
      return measurements.length ? { kind: 'scooter', summary, rows: measurements } : null;
    }
    if (/\bcrosse\b/.test(name) && !/mini|porte|ruban|tape/.test(name)) return { kind: 'hockey', summary: pairs.slice(0, 2), rows: pairs };
    if (/\b(?:rollers?|patins?)\b/.test(name) && !/roue|roulement|frein|sac|lacets|platine|vis|casque|helmet|genou|protege|coud|poignet|protection|short|chaussette|gants|pantalon/.test(name) && options.some(o => /taille|pointure|size/i.test(optionName(o)))) {
      return { kind: 'footwear', summary: pairs.filter(o => /taille|pointure|size/i.test(o.label)), rows: pairs };
    }
    return null;
  }
  function cardFacts(product, variant) {
    const name=fold(product.title),values=variant?.options || [variant?.option1,variant?.option2,variant?.option3];
    const result=[];
    (product.options || []).forEach((option,i)=>{
      let label=typeof option==='string'?option:option.name, value=values[i];
      if(!value || /^(couleur|color|colour|coloris|title)$/i.test(label) || value==='Default Title')return;
      if(/crosse/.test(name)&&/^[LR]\d{2,3}$/i.test(value)){label='Flex';value=value.slice(1);}
      else if(/^(droitier|gaucher)$/i.test(value))label='Côté';
      else if(/(?:skateboard|planche)/.test(name)&&/^(style|largeur)$/i.test(label))label='Largeur';
      else if(/(?:roller|patin)/.test(name)&&/^(taille|size)$/i.test(label))label='Pointure';
      result.push({label,value});
    });
    return result.slice(0,2);
  }
  function compactCardFacts(product, facts) {
    const board = /skateboard|longboard|cruiser|surfskate|planche/.test(fold([product.title, product.product_type, ...(Array.isArray(product.tags) ? product.tags : [])].join(' ')));
    const metric = value => String(value).trim().match(/^(\d+(?:[.,]\d+)?)\s*(cm|mm)(?:\s*\([^)]*\))?$/i);
    const dimensions = facts.map(row => metric(row.value));
    if (board && facts.length === 2 && /^(taille|size|longueur|length)$/i.test(facts[0].label) && /^(largeur|width|style)$/i.test(facts[1].label) && dimensions.every(Boolean) && dimensions[0][2].toLowerCase() === dimensions[1][2].toLowerCase()) {
      return dimensions[0][1].replace('.', ',') + ' × ' + dimensions[1][1].replace('.', ',') + ' ' + dimensions[0][2].toLowerCase();
    }
    return facts.map(row => {
      const match = metric(row.value);
      const value = match ? match[1].replace('.', ',') + ' ' + match[2].toLowerCase() : row.value;
      const label = row.label.replace(/^Largeur$/i, 'Larg.').replace(/^Longueur$/i, 'Long.');
      return label + ' ' + value;
    }).join(' · ');
  }
  function showCardFacts(card, product, variant) {
    const container=card.querySelector('.card > .card__content > .card__information .card-information');
    if(!container)return;
    let line=container.querySelector('.cabane-card-facts');
    const facts=cardFacts(product,variant);
    if(!facts.length){line?.remove();return;}
    if(!line){line=document.createElement('p');line.className='cabane-card-facts';container.prepend(line);}
    const full=facts.map(row=>row.label+' : '+row.value).join(' · ');
    const value=compactCardFacts(product,facts);
    line.title=full;
    line.setAttribute('aria-label',full);
    if(line.textContent!==value)line.textContent=value;
  }
  function enhanceCards(root=document) {
    const cards=[...(root.matches?.('.cabane-product-card')?[root]:[]),...(root.querySelectorAll?.('.cabane-product-card') || [])];
    for(const card of cards){
      if(card.dataset.cabaneFactsReady)continue;
      try {
        const p=JSON.parse(card.querySelector('[data-cabane-card-data]').textContent);
        const selected=Number(new URL(p.url,location.origin).searchParams.get('variant'));
        const v=p.variants.find(v=>v.id===selected)||p.variants.find(v=>v.available)||p.variants[0];
        showCardFacts(card,p,v);card.dataset.cabaneFactsReady='true';
      }catch{}
    }
  }
  const api = { title, choiceAdvice, cardFacts, compactCardFacts, showCardFacts };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined') return;
  window.CabanePresentation = api;
  function enhanceTitles(root = document) {
    const selector = '[data-cabane-full-title], .lcg-search [data-search-default] .lcg-search-product__copy strong';
    const nodes = [...(root.matches?.(selector) ? [root] : []), ...(root.querySelectorAll?.(selector) || [])];
    for (const node of nodes) {
      const full = node.dataset.cabaneFullTitle || node.textContent.trim(), short = title(full);
      node.dataset.cabaneFullTitle = full;
      if (node.matches('.lcg-search-product__copy strong')) {
        const link = node.closest('a');
        if (link) { link.title = full; link.setAttribute('aria-label', full); }
      }
      if (node.textContent !== short) node.textContent = short;
      node.title = full;
      node.setAttribute('aria-label', full);
    }
  }
  enhanceTitles();
  enhanceCards();
  new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) { enhanceTitles(node); enhanceCards(node); }
  }).observe(document.documentElement, { childList: true, subtree: true });
  if (!customElements.get('cabane-choice-advice')) customElements.define('cabane-choice-advice', class extends HTMLElement {
    connectedCallback() { queueMicrotask(() => {
      try {
        this.product = JSON.parse(this.querySelector('script').textContent);
        this.render(this.product.selected_variant);
        this.unsubscribe?.();
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (event.data.sectionId === this.dataset.section) this.render(event.data.variant);
        });
      } catch { this.hidden = true; }
    }); }
    disconnectedCallback() { this.unsubscribe?.(); }
    render(variant) {
      const advice = choiceAdvice(this.product, variant, window.CabaneProductData);
      this.hidden = !advice;
      if (!advice) return;
      for (const message of this.querySelectorAll('[data-advice-kind]')) message.hidden = message.dataset.adviceKind !== advice.kind;
      const summary = this.querySelector('[data-advice-summary]');
      summary.textContent = advice.summary.map(r => `${window.CabaneI18n.text(r.label)} : ${window.CabaneI18n.text(r.value)}`).join(' · ');
      summary.hidden = !summary.textContent;
      const list = this.querySelector('dl'); list.replaceChildren();
      for (const row of advice.rows) { const pair = document.createElement('div'), dt = document.createElement('dt'), dd = document.createElement('dd'); dt.textContent = window.CabaneI18n.text(row.label); dd.textContent = window.CabaneI18n.text(row.value); pair.append(dt, dd); list.append(pair); }
    }
  });
})();
